import { cn } from '../../../../../../lib/utils';
import { primaryText, secondaryText, smallTextSize } from '../../../../../../styling/stylingHelper';
import type { UnlinkedFactuurOrder } from '../../../../../../services/admin/adminService';
import type { TFn } from '../../types';

// Tab 4: factuur orders that never linked to an orderhistory row. Empty IS the happy path — a
// non-empty list is always a bug to investigate (delete-order + reprocess can orphan factuur_orders).
// Presentational: the parent owns the fetch (it is cheap — a partial index over order_history_id IS NULL —
// and is loaded eagerly so the tab strip can always show a count, since any non-zero count is a bug).
export function AICheckerUnlinkedFactuurOrders({ orders, loading, error, t }: { orders: UnlinkedFactuurOrder[] | null; loading: boolean; error: string | null; t: TFn }) {
  if (loading) return <div className={cn(secondaryText, smallTextSize, 'text-center py-8')}>{t('adminTesting.review.unlinkedLoading')}</div>;
  if (error) return <div className={cn('text-center py-8 text-red-500 dark:text-textRed-dark', smallTextSize)}>{error}</div>;
  if (!orders || orders.length === 0) {
    return <div className={cn(secondaryText, smallTextSize, 'text-center py-8')}>🎉 {t('adminTesting.review.unlinkedEmpty')}</div>;
  }

  return (
    <div className="space-y-3">
      <p className={cn(secondaryText, 'text-[11px] italic')}>{t('adminTesting.review.unlinkedCaption')}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className={cn(secondaryText, 'border-b border-gray-200 dark:border-gray-600')}>
              <th className="text-left py-1 px-2">{t('adminTesting.review.unlinkedColInvoice')}</th>
              <th className="text-left py-1 px-2">{t('adminTesting.review.unlinkedColPlatform')}</th>
              <th className="text-left py-1 px-2">{t('adminTesting.review.unlinkedColRestaurant')}</th>
              <th className="text-left py-1 px-2">{t('adminTesting.review.unlinkedColOrder')}</th>
              <th className="text-left py-1 px-2">{t('adminTesting.review.unlinkedColDate')}</th>
              <th className="text-right py-1 px-2">{t('adminTesting.review.unlinkedColProducts')}</th>
              <th className="text-right py-1 px-2">{t('adminTesting.review.unlinkedColTotal')}</th>
              <th className="text-left py-1 px-2">{t('adminTesting.review.unlinkedColStatus')}</th>
              <th className="py-1 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o, oi) => (
              <tr key={o.factuurOrderId} className={cn('border-b border-gray-100 dark:border-gray-700', oi % 2 === 0 ? 'bg-gray-50/50 dark:bg-gray-800/30' : '')}>
                <td className={cn(primaryText, 'py-1 px-2 font-mono')}>{o.invoiceNumber}</td>
                <td className={cn(primaryText, 'py-1 px-2 font-mono')}>{o.platform}</td>
                <td className={cn(primaryText, 'py-1 px-2 max-w-[14rem] truncate')} title={o.restaurantName}>{o.restaurantName}</td>
                <td className={cn(secondaryText, 'py-1 px-2 font-mono')}>{o.orderNumber}</td>
                <td className={cn(secondaryText, 'py-1 px-2 font-mono whitespace-nowrap')}>{o.orderDate}</td>
                <td className={cn(primaryText, 'py-1 px-2 text-right font-mono')}>{o.productCount}</td>
                <td className={cn(primaryText, 'py-1 px-2 text-right font-mono')}>{o.total === null ? '-' : `€${o.total.toFixed(2)}`}</td>
                <td className="py-1 px-2">
                  <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap',
                    o.status === 'order_missing'
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                      : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300')}>
                    {o.status === 'order_missing' ? t('adminTesting.review.unlinkedStatusOrderMissing') : t('adminTesting.review.unlinkedStatusRelinkable')}
                  </span>
                </td>
                <td className="py-1 px-2 text-right">
                  {o.sourceFileUrl && (
                    <a
                      href={o.sourceFileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
                    >
                      {t('adminTesting.review.unlinkedViewFile')}
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
